import React from 'react';
import { Reveal } from './UI';
import SplitText from './SplitText';

const ITEMS = [
  {
    num: '01',
    title: 'Encrypted in transit and at rest',
    body: "Video and event data are encrypted end to end — on the way in and while stored. Your footage doesn't travel unprotected.",
  },
  {
    num: '02',
    title: 'Role-based access control',
    body: 'Only the right people see the right feeds and events — configurable by site, zone and role. Granular access with a full audit trail.',
  },
  {
    num: '03',
    title: 'Retention you control',
    body: 'Footage and event logs are kept only as long as you decide, then purged on your schedule. No data left sitting around beyond its purpose.',
  },
  {
    num: '04',
    title: 'On-prem or private-cloud options',
    body: "For sites where video can't leave the premises, processing can stay local. Ask us about edge deployment for air-gapped environments.",
  },
];

/* Corner bracket SVG — accent top-right of each row */
function CornerBracket() {
  return (
    <svg
      width="16" height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M16 0 L16 16"
        stroke="#7C3AED" strokeWidth="2"
      />
      <path
        d="M0 0 L16 0"
        stroke="#7C3AED" strokeWidth="2"
      />
    </svg>
  );
}

export default function Security() {
  return (
    <section id="security" className="section section-cream">
      <style>{`
        #security [class*="eyebrow"]::before,
        #security [class*="eyebrow"]::after {
          content: none !important;
          display: none !important;
        }

        /* ── Page-width container ── */
        #security .sec-outer {
          max-width: 1520px;
          margin: 0 auto;
          padding-inline: clamp(16px, 3vw, 24px);
        }

        /* ── Two-column layout ── */
        #security .sec-layout {
          display: grid;
          grid-template-columns: 400px 1fr;
          align-items: start;
          gap: 0;
        }

        /* ── Left panel ── */
        #security .sec-left {
          position: sticky;
          top: 24px;
          padding-right: clamp(32px, 4vw, 64px);
          padding-top: 6px;
          border-right: 1px solid var(--border-cream, #DDD8D0);
          align-self: stretch;
        }

        #security .sec-left-inner {
          position: sticky;
          top: 96px;
        }

        #security .sec-title {
          font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
          font-size: clamp(26px, 3.2vw, 42px);
          font-weight: 700;
          color: var(--ink, #111);
          line-height: 1.18;
          margin: 0 0 14px;
        }

        #security .sec-sub {
          font-size: clamp(13.5px, 1vw, 15.5px);
          color: var(--ink-soft, #111111);
          line-height: 1.65;
          margin: 0 0 36px;
          max-width: 300px;
        }

        #security .sec-cta {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 15px;
          font-weight: 600;
          color: var(--ink, #111);
          text-decoration: none;
          border-bottom: 1.5px solid currentColor;
          padding-bottom: 2px;
          transition: opacity 0.2s;
        }
        #security .sec-cta:hover { opacity: 0.5; }

        #security .sec-dot {
          width: 15px;
          height: 15px;
          border-radius: 50%;
          border: 1.5px solid #7C3AED;
          flex-shrink: 0;
        }

        /* ── Right panel ── */
        #security .sec-right {
          padding-left: clamp(28px, 4vw, 60px);
          padding-top: 6px;
        }

        /* ── Each row ── */
        #security .sec-row {
          border-bottom: 1px solid var(--border-cream, #DDD8D0);
          padding: 28px 0;
          display: flex;
          align-items: flex-start;
          gap: 20px;
          position: relative;
        }

        #security .sec-row:first-child {
          border-top: 1px solid var(--border-cream, #DDD8D0);
        }

        /* Corner bracket — absolute top-right */
        #security .sec-corner {
          position: absolute;
          top: 20px;
          right: 0;
        }

        /* ── Number badge ── */
        #security .sec-num {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          border: 1px solid #DDD6FE;
          background: #F5F3FF;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: var(--font-mono, monospace);
          font-size: 12px;
          font-weight: 700;
          color: #7C3AED;
          letter-spacing: -0.03em;
          flex-shrink: 0;
        }

        /* ── Text block ── */
        #security .sec-text {
          flex: 1;
          padding-right: 28px;
        }

        #security .sec-row-title {
          font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
          font-size: clamp(17.5px, 1.5vw, 22px);
          font-weight: 700;
          color: #7C3AED !important;
          margin: 0 0 8px;
          line-height: 1.25;
        }

        #security .sec-row-body {
          font-size: clamp(13.5px, 1vw, 15px);
          line-height: 1.7;
          color: var(--ink-soft, #111111);
          margin: 0;
        }

        /* ── Tablet ── */
        @media (max-width: 860px) {
          #security .sec-layout {
            grid-template-columns: 1fr;
          }

          #security .sec-left {
            position: static;
            border-right: none;
            border-bottom: 1px solid var(--border-cream, #DDD8D0);
            padding-right: 0;
            padding-bottom: 36px;
            margin-bottom: 36px;
            align-self: auto;
          }

          #security .sec-left-inner {
            position: static;
          }

          #security .sec-right {
            padding-left: 0;
          }

          #security .sec-sub {
            max-width: 100%;
          }
        }

        /* ── Mobile ── */
        @media (max-width: 480px) {
          #security .sec-row {
            padding: 22px 0;
            gap: 14px;
          }

          #security .sec-text {
            padding-right: 20px;
          }
        }
      `}</style>

      <div className="sec-outer">
        <div className="sec-layout">

          {/* Left */}
          <div className="sec-left">
            <Reveal>
              <div className="sec-left-inner">
                <h2 className="sec-title">
                  <span>Your footage. </span>
                  <span className="sh2-purple">Your rules.</span>
                </h2>
                <SplitText
                  tag="p"
                  text="Video from your floor is sensitive — for the business and the people on it. TruVue AI is built around that, not in spite of it."
                  delay={12}
                  duration={0.7}
                  ease="power2.out"
                  splitType="words"
                  from={{ opacity: 0, y: 15 }}
                  to={{ opacity: 1, y: 0 }}
                  threshold={0.1}
                  rootMargin="-40px"
                  textAlign="left"
                  className="sec-sub"
                />
                <a href="https://wa.me/919425074535" target="_blank" rel="noopener noreferrer" className="sec-cta">
                  <span className="sec-dot" />
                  Book a Demo
                </a>
              </div>
            </Reveal>
          </div>

          {/* Right */}
          <div className="sec-right">
            {ITEMS.map((item, i) => (
              <Reveal key={item.num} delay={i * 70}>
                <div className="sec-row">
                  {/* Accent corner bracket */}
                  <span className="sec-corner">
                    <CornerBracket />
                  </span>

                  {/* Circle number */}
                  <div className="sec-num">{item.num}</div>

                  {/* Text */}
                  <div className="sec-text">
                    <SplitText
                      tag="h3"
                      text={item.title}
                      delay={25}
                      duration={0.8}
                      ease="power3.out"
                      splitType="words"
                      from={{ opacity: 0, y: 18 }}
                      to={{ opacity: 1, y: 0 }}
                      threshold={0.1}
                      rootMargin="-40px"
                      textAlign="left"
                      className="sec-row-title"
                    />
                    <SplitText
                      tag="p"
                      text={item.body}
                      delay={10}
                      duration={0.7}
                      ease="power2.out"
                      splitType="words"
                      from={{ opacity: 0, y: 12 }}
                      to={{ opacity: 1, y: 0 }}
                      threshold={0.1}
                      rootMargin="-40px"
                      textAlign="left"
                      className="sec-row-body"
                    />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}